import type { ReactNode } from "react";
import { Code } from "@connectrpc/connect";
import { createConnectQueryKey, useQuery } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import { formatDate, formatNepalTime, nepalIsoDate } from "@yatayat/core/date";
import { StaffRole } from "@yatayat/gen/yatayat/auth/v1/auth_pb";
import { BookingService } from "@yatayat/gen/yatayat/booking/v1/booking_pb";
import { OperatorService, type Trip } from "@yatayat/gen/yatayat/operator/v1/operator_pb";

import { CounterSale } from "~/components/OperatorCounterSale";
import { TripManifest } from "~/components/OperatorManifest";
import { PermitWarning, TripStatusBadge, TripVehicle, useStaffSessionExpiry } from "~/components/OperatorTrip";
import { Loading, QueryError } from "~/components/QueryState";
import { TripDeparture } from "~/components/TripDeparture";
import { VehicleSwap } from "~/components/VehicleSwap";
import { useI18n } from "~/lib/i18n";
import { useTripChanges } from "~/lib/live";
import { errorCode, retryTransient } from "~/lib/rpc/errors";
import { can, useStaffSession } from "~/lib/staff";
import { ui } from "~/lib/ui";

export const Route = createFileRoute("/operator/trips/$tripId/")({
  component: OperatorTripPage,
});

function OperatorTripPage() {
  const { tripId } = Route.useParams();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const role = useStaffSession().session?.role ?? StaffRole.UNSPECIFIED;

  // The live feed is the fast path; polling covers a dropped connection.
  const manifest = useQuery(OperatorService.method.getManifest, { tripId }, { retry: retryTransient, refetchInterval: 15_000 });
  const refetchManifest = manifest.refetch;

  useTripChanges(tripId, () => {
    void refetchManifest();
    void queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({ schema: BookingService.method.getSeatMap, input: { tripId }, cardinality: undefined }),
    });
    void queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({ schema: OperatorService.method.listTrips, cardinality: undefined }),
    });
  });

  const expired = useStaffSessionExpiry(manifest.error);
  const errCode = manifest.isError ? errorCode(manifest.error) : undefined;

  const trip = manifest.data?.trip;
  const backLink = (
    <Link
      to="/operator"
      search={trip ? { date: nepalIsoDate(Number(trip.departsAtMs)) } : {}}
      className="text-sm text-ink underline-offset-2 hover:underline dark:text-ink-dark"
    >
      <span aria-hidden="true">← </span>
      {t("allTrips")}
    </Link>
  );

  if (!manifest.data || !trip) {
    let body: ReactNode;
    if (manifest.isPending || expired) {
      body = <Loading />;
    } else if (errCode === Code.NotFound || errCode === Code.InvalidArgument || errCode === Code.PermissionDenied) {
      body = <p className="text-slate-600 dark:text-slate-400">{t("operatorTripNotFound")}</p>;
    } else {
      body = <QueryError onRetry={() => void manifest.refetch()} busy={manifest.isFetching} />;
    }

    return (
      <div className="mx-auto max-w-3xl space-y-3">
        {backLink}
        {body}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div className="space-y-4">
        {backLink}
        <TripHeader trip={trip} />
        {can.board(role) && (
          <Link
            to="/operator/trips/$tripId/scan"
            params={{ tripId }}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-4 py-3 text-base font-semibold text-white sm:w-auto sm:justify-start dark:bg-ink-dark dark:text-slate-950"
          >
            {t("scanBoarding")}
            <span aria-hidden="true">→</span>
          </Link>
        )}
        {/* A different trip starts afresh. */}
        {can.manage(role) && <VehicleSwap key={trip.id} trip={trip} />}
      </div>

      {can.board(role) && (
        <section aria-labelledby="departure" className="space-y-3">
          <h2 id="departure" className="text-lg font-semibold">
            {t("departureTitle")}
          </h2>
          {/* A different trip starts afresh. */}
          <TripDeparture key={trip.id} trip={trip} />
        </section>
      )}

      {can.sell(role) && (
        <section aria-labelledby="counter-sale" className="space-y-4">
          <h2 id="counter-sale" className="text-lg font-semibold">
            {t("sellTitle")}
          </h2>
          {/* A different trip starts the sale afresh. */}
          <CounterSale key={trip.id} trip={trip} />
        </section>
      )}

      <section aria-labelledby="manifest" className="space-y-4">
        <h2 id="manifest" className="text-lg font-semibold">
          {t("manifestTitle")}
        </h2>
        <TripManifest manifest={manifest.data} />
      </section>
    </div>
  );
}

function TripHeader({ trip }: { trip: Trip }) {
  const { t, lang } = useI18n();
  const departs = Number(trip.departsAtMs);
  const origin = trip.stops[0]?.code;
  const last = trip.stops[trip.stops.length - 1]?.code;

  return (
    <header className="space-y-3 rounded-xl border border-slate-200 p-4 dark:border-slate-800">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-slate-600 dark:text-slate-400">{formatDate(departs, lang)}</p>
          <h1 className={ui.h1}>
            <time dateTime={new Date(departs).toISOString()}>{formatNepalTime(departs, lang)}</time>
            {" · "}
            {trip.routeName?.[lang] || trip.routeCode}
          </h1>
          <TripVehicle trip={trip} />
        </div>
        <TripStatusBadge status={trip.status} />
      </div>
      <PermitWarning trip={trip} />
      {origin && last && origin !== last && (
        <Link
          to="/book/$tripId"
          params={{ tripId: trip.id }}
          search={{ from: origin, to: last }}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-sm text-ink underline underline-offset-2 dark:text-ink-dark"
        >
          {t("viewOnCitizenSite")}
          <span aria-hidden="true"> ↗</span>
          <span className="sr-only"> {t("opensNewTab")}</span>
        </Link>
      )}
    </header>
  );
}
